import { createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { api, type ReferralProgress, type ReferralSnapshot } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'
import styles from './home.module.css'

export const Route = createFileRoute('/settings')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: SettingsRoute,
})

function SettingsRoute() {
  const [trialState, setTrialState] = useState<{
    state: 'not_started' | 'active' | 'expired'
    daysLeft: number
  } | null>(null)
  const [subscriptionState, setSubscriptionState] = useState<{
    state: string
    plan: 'monthly' | 'yearly'
    currentPrice: { list: number; effective: number; milestoneDiscountRate: number }
  } | null>(null)
  const [creditBalance, setCreditBalance] = useState<number | null>(null)
  const [consentState, setConsentState] = useState<{
    analyticsAllowed: boolean
    marketingAllowed: boolean
    functionalAllowed: boolean
  } | null>(null)
  const [referralSnapshot, setReferralSnapshot] = useState<ReferralSnapshot | null>(null)
  const [referralProgress, setReferralProgress] = useState<ReferralProgress | null>(null)

  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [isAccountBusy, setIsAccountBusy] = useState(false)

  const [extensionInstalled, setExtensionInstalled] = useState(true)
  const [extensionConnected, setExtensionConnected] = useState(true)
  const [extensionSyncAt, setExtensionSyncAt] = useState<string>(new Date().toISOString())
  const [isExtensionBusy, setIsExtensionBusy] = useState(false)

  const loadAccountState = async (): Promise<void> => {
    const results = await Promise.allSettled([
      api.getTrial(),
      api.getSubscription(),
      api.getCredits(),
      api.getConsent(),
      api.referralMe(),
      api.referralProgress(),
    ])

    const [trial, subscription, credits, consent, referral, progress] = results
    const failedPanels = results.filter((result) => result.status === 'rejected').length

    if (trial.status === 'fulfilled') {
      setTrialState({
        state: trial.value.state,
        daysLeft: trial.value.daysLeft,
      })
    }

    if (subscription.status === 'fulfilled') {
      setSubscriptionState({
        state: subscription.value.state,
        plan: subscription.value.plan,
        currentPrice: subscription.value.currentPrice,
      })
    }

    if (credits.status === 'fulfilled') {
      setCreditBalance(credits.value.balance)
    }

    if (consent.status === 'fulfilled') {
      setConsentState({
        analyticsAllowed: consent.value.analyticsAllowed,
        marketingAllowed: consent.value.marketingAllowed,
        functionalAllowed: consent.value.functionalAllowed,
      })
    }

    if (referral.status === 'fulfilled') {
      setReferralSnapshot(referral.value)
    }

    if (progress.status === 'fulfilled') {
      setReferralProgress(progress.value)
    }

    if (failedPanels > 0) {
      setErrorMessage('Some settings data is temporarily unavailable.')
    } else {
      setErrorMessage(null)
    }
  }

  useEffect(() => {
    const bootstrap = async (): Promise<void> => {
      try {
        await loadAccountState()
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Failed to load settings.')
      }
    }

    void bootstrap()
  }, [])

  const handleToggleAnalyticsConsent = async (): Promise<void> => {
    if (!consentState) {
      return
    }

    setIsAccountBusy(true)
    setErrorMessage(null)

    try {
      await api.updateConsent({
        analyticsAllowed: !consentState.analyticsAllowed,
        marketingAllowed: consentState.marketingAllowed,
        functionalAllowed: consentState.functionalAllowed,
      })
      setNotice('Consent preference updated.')
      await loadAccountState()
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update consent.')
    } finally {
      setIsAccountBusy(false)
    }
  }

  const handleUpgradeYearly = async (): Promise<void> => {
    setIsAccountBusy(true)
    setErrorMessage(null)

    try {
      await api.changePlan({
        targetPlan: 'yearly',
        effectiveMode: 'immediate',
      })
      setNotice('Plan updated.')
      await loadAccountState()
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Plan change failed.')
    } finally {
      setIsAccountBusy(false)
    }
  }

  const handleCopyReferralLink = async (): Promise<void> => {
    if (!referralSnapshot) {
      return
    }

    try {
      await navigator.clipboard.writeText(referralSnapshot.referralLink)
      setNotice('Referral link copied.')
    } catch {
      setNotice(`Copy this link: ${referralSnapshot.referralLink}`)
    }
  }

  const handleCopyReferralMessage = async (): Promise<void> => {
    if (!referralSnapshot) {
      return
    }

    const message = `I use StatureBoost to run my weekly LinkedIn system. You can try it here: ${referralSnapshot.referralLink}`

    try {
      await navigator.clipboard.writeText(message)
      setNotice('Invite message copied.')
    } catch {
      setNotice(message)
    }
  }

  const handleRegenerateReferralLink = async (): Promise<void> => {
    setIsAccountBusy(true)
    setNotice(null)
    setErrorMessage(null)

    try {
      const refreshed = await api.regenerateReferralLink()
      setReferralSnapshot((current) =>
        current
          ? {
              ...current,
              referralCode: refreshed.referralCode,
              referralLink: refreshed.referralLink,
            }
          : {
              referralCode: refreshed.referralCode,
              referralLink: refreshed.referralLink,
              milestone: {
                qualifiedCount: 0,
                discountRate: 0,
                nextTarget: 1,
              },
            }
      )
      setNotice('Referral link regenerated.')
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Could not regenerate referral link.'
      )
    } finally {
      setIsAccountBusy(false)
    }
  }

  const handleInstallExtension = async (): Promise<void> => {
    setIsExtensionBusy(true)
    await new Promise((resolve) => setTimeout(resolve, 450))
    setExtensionInstalled(true)
    setIsExtensionBusy(false)
    setNotice('Extension installed. Connect your LinkedIn tab to start sync.')
  }

  const handleConnectExtension = async (): Promise<void> => {
    if (!extensionInstalled) {
      return
    }

    setIsExtensionBusy(true)
    await new Promise((resolve) => setTimeout(resolve, 550))
    setExtensionConnected(true)
    setExtensionSyncAt(new Date().toISOString())
    setIsExtensionBusy(false)
    setNotice('LinkedIn connected. Passive collection is active.')
  }

  const handleSyncNow = async (): Promise<void> => {
    if (!extensionConnected) {
      return
    }

    setIsExtensionBusy(true)
    await new Promise((resolve) => setTimeout(resolve, 700))
    setExtensionSyncAt(new Date().toISOString())
    setIsExtensionBusy(false)
    setNotice('Fresh profile and post metrics synced from extension.')
  }

  return (
    <section className={styles.layout}>
      <header className={styles.hero}>
        <p className={styles.overline}>Settings</p>
        <h1 className={styles.pageTitle}>Account, sync, and controls</h1>
        <p className={styles.subtitle}>
          Manage subscription state, privacy controls, referral incentives, and extension sync
          health.
        </p>
      </header>

      {notice && <p className={styles.notice}>{notice}</p>}
      {errorMessage && <p className={styles.error}>{errorMessage}</p>}

      <section className={styles.extensionCard}>
        <div className={styles.templatesHeader}>
          <h2>Browser extension</h2>
          <p>
            {extensionConnected ? 'Connected' : extensionInstalled ? 'Installed' : 'Not installed'}
          </p>
        </div>
        <p className={styles.muted}>
          Data collection runs in the browser extension. The server does not scrape LinkedIn.
        </p>
        <div className={styles.extensionStatusRow}>
          <p>
            <strong>Install:</strong> {extensionInstalled ? 'Complete' : 'Required'}
          </p>
          <p>
            <strong>Connection:</strong> {extensionConnected ? 'Healthy' : 'Needs reconnect'}
          </p>
          <p>
            <strong>Last sync:</strong> {new Date(extensionSyncAt).toLocaleString()}
          </p>
        </div>
        <div className={styles.accountActions}>
          <button
            type="button"
            onClick={() => void handleInstallExtension()}
            disabled={isExtensionBusy || extensionInstalled}
          >
            Install extension
          </button>
          <button
            type="button"
            onClick={() => void handleConnectExtension()}
            disabled={isExtensionBusy || !extensionInstalled || extensionConnected}
          >
            Connect LinkedIn
          </button>
          <button
            type="button"
            onClick={() => void handleSyncNow()}
            disabled={isExtensionBusy || !extensionConnected}
          >
            Sync now
          </button>
        </div>
      </section>

      <section className={styles.accountCard}>
        <div className={styles.accountGrid}>
          <div>
            <p className={styles.accountLabel}>Trial</p>
            <p className={styles.accountValue}>
              {trialState ? `${trialState.state} (${trialState.daysLeft} days left)` : 'Loading...'}
            </p>
          </div>
          <div>
            <p className={styles.accountLabel}>Plan</p>
            <p className={styles.accountValue}>
              {subscriptionState
                ? `${subscriptionState.plan} • $${subscriptionState.currentPrice.effective.toFixed(2)}${
                    subscriptionState.currentPrice.milestoneDiscountRate > 0
                      ? ` • ${(subscriptionState.currentPrice.milestoneDiscountRate * 100).toFixed(0)}% tier`
                      : ''
                  }`
                : 'Loading...'}
            </p>
          </div>
          <div>
            <p className={styles.accountLabel}>Credits</p>
            <p className={styles.accountValue}>{creditBalance ?? 'Loading...'}</p>
          </div>
          <div>
            <p className={styles.accountLabel}>Referrals</p>
            <p className={styles.accountValue}>
              {referralSnapshot
                ? `${referralSnapshot.milestone.qualifiedCount} qualified`
                : 'Loading...'}
            </p>
          </div>
        </div>

        {referralSnapshot && (
          <p className={styles.subtitle}>
            Code <code>{referralSnapshot.referralCode}</code> • next milestone:{' '}
            {referralSnapshot.milestone.nextTarget ?? 'max tier reached'}
          </p>
        )}

        <div className={styles.accountActions}>
          <button type="button" onClick={handleUpgradeYearly} disabled={isAccountBusy}>
            Switch to yearly
          </button>
          <button type="button" onClick={handleToggleAnalyticsConsent} disabled={isAccountBusy}>
            {consentState?.analyticsAllowed ? 'Disable analytics' : 'Enable analytics'}
          </button>
          <button
            type="button"
            onClick={() => void handleCopyReferralLink()}
            disabled={!referralSnapshot}
          >
            Copy referral link
          </button>
          <button
            type="button"
            onClick={() => void handleCopyReferralMessage()}
            disabled={!referralSnapshot}
          >
            Copy invite message
          </button>
          <button
            type="button"
            onClick={() => void handleRegenerateReferralLink()}
            disabled={isAccountBusy}
          >
            Regenerate referral link
          </button>
        </div>
      </section>

      {referralProgress && (
        <section className={styles.analysisCard}>
          <h2>Referral progress</h2>
          <p>
            Qualified: <strong>{referralProgress.qualifiedCount}</strong> • Current tier:{' '}
            <strong>{(referralProgress.currentDiscountRate * 100).toFixed(0)}%</strong> • Next
            target: <strong>{referralProgress.nextMilestoneTarget ?? 'max tier reached'}</strong>
          </p>
          <div className={styles.templateList}>
            {referralProgress.events.slice(0, 4).map((event) => (
              <article key={event.id} className={styles.templateRow}>
                <div>
                  <h3>
                    {event.state} • {event.referralCode}
                  </h3>
                  <p>
                    referred {event.referredUserId} • {new Date(event.createdAt).toLocaleString()}
                  </p>
                </div>
              </article>
            ))}
            {referralProgress.events.length === 0 && (
              <p className={styles.muted}>No referral events yet.</p>
            )}
          </div>
        </section>
      )}
    </section>
  )
}
